import { Box } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { FC } from "react";
import { EmployerFundTopUp } from "../../../models/funding/employerFundTopUp";
import {
  EmployerPaymentMethod,
  EmployerPaymentMethodType
} from "../../../models/funding/employerPaymentMethod";
import {
  formatCurrency,
  formatDate,
  formatPercentage
} from "../../../utils/formatters";
import { toTitleCase } from "../../../utils/toTitleCase";
import { useData } from "../../../utils/useData";
import { handlePaymentMethodDisplayString } from "../../funding/PaymentMethodPreview";
import { LoadingSpinner } from "../../shared/LoadingSpinner";
import { Table } from "../../shared/Table";

export const handleTopUpPaymentMethodDisplayString = (
  topUp: EmployerFundTopUp,
  paymentMethods?: EmployerPaymentMethod[]
) => {
  if (topUp.paymentMethodType === EmployerPaymentMethodType.STATEMENT_INVOICE) {
    return "Statement Invoice";
  }

  if (topUp.paymentMethodType === EmployerPaymentMethodType.PURCHASE_ORDER) {
    return "Purchase Order";
  }

  if (topUp.paymentMethodType === EmployerPaymentMethodType.FLOAT_TRANSFER) {
    return "Float Transfer";
  }

  const paymentMethod = paymentMethods?.find(
    (pm) => pm.id === topUp.paymentMethodId
  );

  return paymentMethod ? handlePaymentMethodDisplayString(paymentMethod) : "-";
};

const EmployerFundTopUpsTable: FC<{
  topUps: EmployerFundTopUp[];
  totalItems: number;
  page: number;
  pageSize: number;
  updatePage: (value: number) => void;
  updatePageSize: (value: number) => void;
  isLoading: boolean;
}> = ({
  topUps,
  totalItems,
  page,
  pageSize,
  updatePage,
  updatePageSize,
  isLoading
}) => {
  const { paymentMethods } = useData();

  const columns: ColumnDef<EmployerFundTopUp>[] = [
    {
      header: "Payment Method",
      cell: ({ row }) => {
        return (
          <Box
            onClick={() => {
              if (row.original.invoiceUrl) {
                window.open(row.original.invoiceUrl, "_blank");
              }
            }}
            sx={{
              textDecoration: row.original.invoiceUrl ? "underline" : "none",
              cursor: row.original.invoiceUrl ? "pointer" : "default",
              color: "primary.main"
            }}
          >
            {handleTopUpPaymentMethodDisplayString(
              row.original,
              paymentMethods.data?.items
            )}
          </Box>
        );
      },
      enableColumnFilter: false
    },
    {
      header: "Status",
      cell: ({ row }) => {
        const titleCase = toTitleCase(row.original.status);
        return <Box>{titleCase}</Box>;
      },
      enableColumnFilter: false
    },
    {
      cell: ({ row }) => (
        <Box>{formatCurrency(row.original.amount.amount)}</Box>
      ),
      header: "Amount",
      enableColumnFilter: false
    },
    {
      cell: ({ row }) => (
        <Box>{formatPercentage(row.original.surcharge * 100)}</Box>
      ),
      header: "Surcharge",
      enableColumnFilter: false
    },
    {
      cell: ({ row }) => (
        <Box>{formatCurrency(row.original.amountWithSurcharge.amount)}</Box>
      ),
      header: "Total",
      enableColumnFilter: false
    },
    {
      header: "Date",
      meta: {
        filterType: "dateRange"
      },
      enableColumnFilter: false,
      cell: ({ row }) => {
        const formattedDate = formatDate(row.original.createdAt);
        return <Box>{formattedDate}</Box>;
      }
    }
  ];

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <Box />
      <Table<EmployerFundTopUp>
        data={topUps}
        totalItems={totalItems}
        columns={columns}
        isServerSidePagination={true}
        paginationPage={page}
        paginationPageSize={pageSize}
        onPageChange={updatePage}
        onPageSizeChange={updatePageSize}
      />
    </>
  );
};

export { EmployerFundTopUpsTable };
